/**
 * Enhanced modal utilities for better mobile support
 * Fixes issues with touch events on mobile devices
 */

/**
 * Handles click/touch events for closing modals
 * Works reliably on both desktop and mobile
 */
export const handleModalClose = (onClose, event) => {
  // Prevent default behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  // Call the close handler
  if (onClose) {
    onClose();
  }
};

/**
 * Handles click/touch events for modal content
 * Prevents closing when clicking inside the modal
 */
export const handleModalContentClick = (event) => {
  event.stopPropagation();
  event.preventDefault();
};

/**
 * Handles overlay click to close modal
 * Works with both mouse and touch events
 */
export const handleOverlayClick = (onClose, event) => {
  // Only close if clicking directly on the overlay, not on children
  if (event.target === event.currentTarget) {
    event.preventDefault();
    event.stopPropagation();
    if (onClose) {
      onClose();
    }
  }
};

/**
 * Enhanced event handler that works on both desktop and mobile
 * Combines onClick, onTouchStart, and onTouchEnd for maximum compatibility
 */
export const createModalHandlers = (onClose) => {
  let touchStartTime = 0;
  let touchStartTarget = null;

  return {
    // Handle overlay click
    onClick: (e) => {
      if (e.target === e.currentTarget) {
        handleOverlayClick(onClose, e);
      }
    },
    
    // Handle touch start
    onTouchStart: (e) => {
      touchStartTime = Date.now();
      touchStartTarget = e.target;
    },
    
    // Handle touch end - close if it was a tap on overlay
    onTouchEnd: (e) => {
      const touchDuration = Date.now() - touchStartTime;
      const isQuickTap = touchDuration < 300; // Less than 300ms
      const isSameTarget = e.target === touchStartTarget;
      
      // Only close if it's a quick tap on the overlay itself
      if (isQuickTap && isSameTarget && e.target === e.currentTarget) {
        e.preventDefault();
        e.stopPropagation();
        if (onClose) {
          onClose();
        }
      }
    }
  };
};

/**
 * Enhanced button click handler for mobile
 */
export const createButtonHandler = (handler) => {
  let touchStartTime = 0;

  return {
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (handler) {
        handler(e);
      }
    },
    onTouchStart: (e) => {
      touchStartTime = Date.now();
      e.preventDefault();
    },
    onTouchEnd: (e) => {
      const touchDuration = Date.now() - touchStartTime;
      if (touchDuration < 300) { // Quick tap
        e.preventDefault();
        e.stopPropagation();
        if (handler) {
          handler(e);
        }
      }
    }
  };
};

/**
 * Prevents body scroll when modal is open
 * Works better on mobile devices
 */
export const preventBodyScroll = (prevent = true) => {
  if (prevent) {
    // Get current scroll position
    const scrollY = window.scrollY;
    
    // Apply styles
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    // Store scroll position for restoration
    document.body.dataset.scrollY = scrollY;
  } else {
    // Restore scroll position
    const scrollY = document.body.dataset.scrollY || 0;
    
    // Remove styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    
    // Restore scroll position
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY, 10));
    }
    
    delete document.body.dataset.scrollY;
  }
};

/**
 * Check if device is mobile
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};



